export interface ApiException {
  domain: string;
  type: string;
  /** The server's own wording for developers — snake_case on the wire, never shown as-is. */
  dev_message?: string;
  data: { [key: string]: string };
}

export const isPermissionDenied = (e: unknown): boolean =>
  !!e &&
  typeof e === 'object' &&
  (e as ApiException).domain === 'MIDDLEWARES' &&
  (e as ApiException).type === 'ROLE';

/**
 * The client throws the `ErrorResponse` body rather than the response, so a 404 has to be
 * recognised from `type` — the backend's NOT_FOUND convention — not from a status code.
 */
export const isNotFound = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as ApiException).type === 'NOT_FOUND';

/**
 * The vessel is on a lifetime plan an admin granted, which the vessel cannot change, buy over,
 * cancel or resume (409). Only reached when the screen was stale — the controls are not drawn
 * for such a plan.
 */
export const isManuallyGrantedPlan = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as ApiException).type === 'SUBSCRIPTION_MANUALLY_GRANTED';

/** What to say when a request was refused because the plan is a lifetime one. */
export const MANUALLY_GRANTED_MESSAGE =
  'This vessel is on a lifetime plan from Vessel Haven, so it can’t be changed here.';

/**
 * Stripe refused the change because the customer has no card on file:
 * `{"domain":"PAYMENTS","type":"INPUTS","dev_message":"Unable to change the subscription
 * because no payment method has been provided"}`. Nothing was altered and nothing was
 * charged, and no amount of retrying gets past it — the card has to be added in the
 * billing portal first — so it needs its own message rather than "please try again".
 *
 * `PAYMENTS`/`INPUTS` is shared with several other rejections (an inactive subscription,
 * one that has already ended, an incomplete harbor upgrade), so the `dev_message` is the
 * only thing that tells them apart. Matched on the phrase rather than the whole sentence:
 * the wording is the server's to change, and a near miss falls back to the generic message.
 */
export const isPaymentMethodMissing = (e: unknown): boolean =>
  !!e &&
  typeof e === 'object' &&
  (e as ApiException).domain === 'PAYMENTS' &&
  (e as ApiException).type === 'INPUTS' &&
  !!(e as ApiException).dev_message?.toLowerCase().includes('payment method');
